<script setup lang="ts">
import { computed } from 'vue'
import { useTheme } from 'vuetify'
import { useChartColors } from '@/utils/chartColors'
import type { ITreemapWidget } from '@/modules/dashboard/types/ITreemapWidget'

interface Props {
  widgetData: ITreemapWidget
  currentTarget?: string
  loading?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  loading: false,
})

const vuetifyTheme = useTheme()
const { generateColors } = useChartColors()

const CHART_HEIGHT = 420

const tiles = computed(() => {
  const points = props.widgetData.data?.series?.[0]?.data || []

  return points
    .map(point => ({
      x: String(point?.x ?? ''),
      y: Number(point?.y ?? 0),
    }))
    .filter(tile => tile.x && tile.y > 0)
})

const chartSeries = computed(() => [{
  data: tiles.value,
}])

const chartOptions = computed(() => {
  const count = tiles.value.length
  const provided = props.widgetData.data?.colors || []
  const colors = provided.length > 0 ? provided : generateColors(count)
  const isDark = vuetifyTheme.current.value.dark

  return {
    chart: {
      type: 'treemap',
      fontFamily: 'Inter, sans-serif',
      toolbar: { show: false },
      parentHeightOffset: 0,
      background: 'transparent',
    },
    legend: { show: false },
    colors,
    plotOptions: {
      treemap: {
        distributed: true,
        enableShades: false,
        /*
         * Иначе Apex считает кегль как sqrt(площадь) / средняя длина подписи
         * и упирается в fontSize. Длинные названия сжимают текст на всех клетках.
         * truncate держит фиксированный кегль и обрезает то, что не влезает.
         */
        dataLabels: {
          format: 'truncate',
        },
      },
    },
    dataLabels: {
      enabled: true,
      style: {
        fontSize: '15px',
        fontFamily: 'Inter, sans-serif',
        fontWeight: 500,
      },
    },
    tooltip: {
      theme: isDark ? 'dark' : 'light',
      y: {
        formatter: (value: number) => String(value),
      },
    },
  }
})
</script>

<template>
  <VCard class="pa-4 h-100 d-flex flex-column">
    <div class="d-flex align-center justify-space-between mb-4 flex-shrink-0">
      <div class="text-h5 d-flex align-center gap-2 min-w-0 text-truncate">
        <div
          v-show="widgetData.show_icon && widgetData.icon"
          class="chart-icon-wrapper flex-shrink-0"
          :style="{ background: widgetData.icon_background }"
        >
          <VIcon :icon="widgetData.icon" size="sm" />
        </div>
        <span class="text-truncate">{{ widgetData.name }}</span>
      </div>
    </div>

    <div v-if="loading" class="flex-grow-1">
      <VSkeletonLoader type="image" class="treemap-skeleton bg-transparent" />
    </div>
    <VueApexCharts
      v-else
      type="treemap"
      :height="CHART_HEIGHT"
      :options="chartOptions"
      :series="chartSeries"
    />
  </VCard>
</template>

<style scoped lang="scss">
.chart-icon-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  padding: 4px;
}

.treemap-skeleton {
  height: 420px;
}
</style>
